"use client";

import type {
  TokenResponse,
  RefreshResponse,
  AdminPubkeyInfo,
  RecoveryPayloadWire,
  RecoverVerifyResponse,
} from "./types";
import {
  generateDEK,
  generateSalt,
  deriveKEK,
  wrapDEK,
  unwrapDEK,
  wrapDEKForAdminPubkey,
  buildRecoveryPayloads,
  normalizeRecoveryCode,
  bytesToBase64,
  base64ToBytes,
} from "./crypto_v2";

// =============================================================================
// Types
// =============================================================================

export interface AuthState {
  accessToken: string;
  refreshToken: string;
  derivedKey: string;              // v1: HKDF-derived key (X-Device-Key)
  dek: string;                     // v2: base64 DEK (X-DEK)
  schemeVersion: string;           // "v1" | "v2" | ""
  email: string;
  name: string;                    // display name (may be "")
  isAdmin: boolean;
  helpViewCount: number;
  adminPubkeys: AdminPubkeyInfo[];
  adminDebugEnabled: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const AUTH_STORAGE_KEY = "trio_auth";
const API_BASE = process.env.NEXT_PUBLIC_API_BASE?.trim() || "";

// PBKDF2 parameters for v1 deriveKey — must match server (600K, SHA-256)
const V1_PBKDF2_ITERATIONS = 600_000;
const V1_PBKDF2_KEY_LENGTH = 32;
const V1_PBKDF2_HASH = "SHA-256";

// =============================================================================
// v1 key derivation (device_key)
// =============================================================================

export async function deriveKey(
  password: string,
  saltBase64: string
): Promise<string> {
  const encoder = new TextEncoder();
  const salt = base64ToBytes(saltBase64);

  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(password),
    "PBKDF2",
    false,
    ["deriveBits"]
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      salt: salt.buffer as ArrayBuffer,
      iterations: V1_PBKDF2_ITERATIONS,
      hash: V1_PBKDF2_HASH,
    },
    keyMaterial,
    V1_PBKDF2_KEY_LENGTH * 8
  );

  return bytesToBase64(new Uint8Array(derivedBits));
}

// =============================================================================
// SessionStorage Management
// =============================================================================

export function getAuthState(): AuthState | null {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(AUTH_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<AuthState>;
    if (!parsed.accessToken || !parsed.refreshToken || !parsed.email) {
      sessionStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
    return {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      derivedKey: parsed.derivedKey ?? "",
      dek: parsed.dek ?? "",
      schemeVersion: parsed.schemeVersion ?? "",
      email: parsed.email,
      name: parsed.name ?? "",
      isAdmin: parsed.isAdmin ?? false,
      helpViewCount: parsed.helpViewCount ?? 0,
      adminPubkeys: parsed.adminPubkeys ?? [],
      adminDebugEnabled: parsed.adminDebugEnabled ?? false,
    };
  } catch {
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
}

export function setAuthState(state: AuthState): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(state));
}

export function clearAuthState(): void {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(AUTH_STORAGE_KEY);
}

// =============================================================================
// Internal: resolve a v1 or v2 token into AuthState, including DEK unwrap
// =============================================================================

async function resolveAuthState(
  email: string,
  password: string,
  token: TokenResponse
): Promise<AuthState> {
  if (token.scheme_version === "v2") {
    if (!token.wrapped_dek || !token.password_salt) {
      throw new Error("v2 login missing wrapped_dek/password_salt");
    }
    const salt = base64ToBytes(token.password_salt);
    const kek = await deriveKEK(password, salt);
    const wrapped = base64ToBytes(token.wrapped_dek);
    const dek = await unwrapDEK(wrapped, kek);
    return {
      accessToken: token.access_token,
      refreshToken: token.refresh_token,
      derivedKey: "",
      dek: bytesToBase64(dek),
      schemeVersion: "v2",
      email,
      name: token.name ?? "",
      isAdmin: token.is_admin ?? false,
      helpViewCount: token.help_view_count ?? 0,
      adminPubkeys: token.admin_pubkeys ?? [],
      adminDebugEnabled: token.admin_debug_enabled ?? false,
    };
  }

  let derivedKeyValue = "";
  if (token.encryption_salt) {
    derivedKeyValue = await deriveKey(password, token.encryption_salt);
  }
  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token,
    derivedKey: derivedKeyValue,
    dek: "",
    schemeVersion: token.scheme_version ?? "",
    email,
    name: token.name ?? "",
    isAdmin: token.is_admin ?? false,
    helpViewCount: token.help_view_count ?? 0,
    adminPubkeys: [],
    adminDebugEnabled: false,
  };
}

// =============================================================================
// Auth Flows — v1 compatibility
// =============================================================================

export async function login(
  email: string,
  password: string
): Promise<TokenResponse> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username: email, passcode: password }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Login failed (${response.status})`);
  }

  const token = (await response.json()) as TokenResponse;
  const state = await resolveAuthState(email, password, token);
  setAuthState(state);
  // Auto-rewrap the admin-debug DEK if the server added a pubkey
  // fingerprint the user hasn't wrapped to yet. Skipped unless v2 +
  // already opted in + stored wraps are stale relative to server's
  // current pubkey list — so this is a no-op for most logins.
  await autoRewrapIfNeeded(token);
  return token;
}

export async function register(
  inviteCode: string,
  email: string,
  password: string
): Promise<TokenResponse> {
  const response = await fetch(`${API_BASE}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      invite_code: inviteCode,
      email,
      password,
    }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Registration failed (${response.status})`);
  }

  const token = (await response.json()) as TokenResponse;
  const state = await resolveAuthState(email, password, token);
  setAuthState(state);
  await autoRewrapIfNeeded(token);
  return token;
}

export async function refreshTokens(): Promise<boolean> {
  const state = getAuthState();
  if (!state) return false;

  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: state.refreshToken }),
    });

    if (!response.ok) {
      clearAuthState();
      return false;
    }

    const refreshed = (await response.json()) as RefreshResponse;

    setAuthState({
      ...state,
      accessToken: refreshed.access_token,
      refreshToken: refreshed.refresh_token,
      name: refreshed.name ?? state.name,
      isAdmin: refreshed.is_admin ?? state.isAdmin,
      helpViewCount: refreshed.help_view_count ?? state.helpViewCount,
      schemeVersion: refreshed.scheme_version ?? state.schemeVersion,
      adminPubkeys: refreshed.admin_pubkeys ?? state.adminPubkeys,
      adminDebugEnabled:
        refreshed.admin_debug_enabled ?? state.adminDebugEnabled,
    });

    await autoRewrapIfNeeded(refreshed);
    return true;
  } catch {
    clearAuthState();
    return false;
  }
}

/**
 * Fire-and-forget admin-debug rewrap if the server says our stored wraps
 * are stale relative to the current admin pubkey list. No-op for:
 *  - non-v2 sessions
 *  - users who haven't opted in to admin debug
 *  - servers where no admin pubkeys are registered
 *
 * Swallows errors on purpose: a failed rewrap is not a login failure;
 * the server will re-flag `admin_rewrap_required` on the next token
 * issue and we'll try again.
 */
async function autoRewrapIfNeeded(
  token: TokenResponse | RefreshResponse
): Promise<void> {
  if (!token.admin_rewrap_required) return;
  if (token.scheme_version !== "v2") return;
  try {
    await adminDebugRewrap();
  } catch {
    // Non-fatal.
  }
}

export async function forgotPassword(email: string): Promise<void> {
  const response = await fetch(`${API_BASE}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed (${response.status})`);
  }
}

export async function resetPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  const response = await fetch(`${API_BASE}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code, new_password: newPassword }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Reset failed (${response.status})`);
  }
}

export function logout(): void {
  clearAuthState();
}

/** Persist a new display name into the stored auth state. No-op when not
 *  logged in. Called after `PUT /account/name` succeeds so the cached
 *  name stays in sync without a full refresh. */
export function updateStoredName(name: string): void {
  const state = getAuthState();
  if (!state) return;
  setAuthState({ ...state, name });
}

// =============================================================================
// v2 auth flows
// =============================================================================

/** Register under v2 scheme. Returns the plaintext recovery codes to display
 *  once; they are NOT stored anywhere and cannot be retrieved again. */
export async function registerV2(
  inviteCode: string,
  email: string,
  password: string
): Promise<{ token: TokenResponse; recoveryCodes: string[] }> {
  const dek = generateDEK();
  const salt = generateSalt();
  const kek = await deriveKEK(password, salt);
  const wrapped = await wrapDEK(dek, kek);
  const { plaintext, wire } = await buildRecoveryPayloads(dek);

  const response = await fetch(`${API_BASE}/auth/register-v2`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      invite_code: inviteCode,
      email,
      password,
      password_salt: bytesToBase64(salt),
      wrapped_dek: bytesToBase64(wrapped),
      recovery_codes: wire,
    }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Registration failed (${response.status})`);
  }

  const token = (await response.json()) as TokenResponse;
  // Use the DEK we just generated directly — round-tripping through the
  // server's wrapped_dek would require a second PBKDF2 (3-5s on mobile) and
  // would throw if the server returned malformed or missing v2 fields, which
  // would swallow the recovery codes. iOS's registerV2 also uses the local
  // DEK directly for the same reason (see AuthManager.swift:723-726).
  const state: AuthState = {
    accessToken: token.access_token,
    refreshToken: token.refresh_token,
    derivedKey: "",
    dek: bytesToBase64(dek),
    schemeVersion: "v2",
    email,
    name: token.name ?? "",
    isAdmin: token.is_admin ?? false,
    helpViewCount: token.help_view_count ?? 0,
    adminPubkeys: token.admin_pubkeys ?? [],
    adminDebugEnabled: token.admin_debug_enabled ?? false,
  };
  setAuthState(state);
  return { token, recoveryCodes: plaintext };
}

/** Change password for a v2 user. Generates a new password_salt, wraps the
 *  existing DEK under the new KEK, generates 8 fresh recovery codes.
 *  Returns the plaintext recovery codes for one-time display. */
export async function changePasswordV2(
  currentPassword: string,
  newPassword: string
): Promise<{ recoveryCodes: string[] }> {
  const state = getAuthState();
  if (!state) throw new Error("Not logged in");
  if (state.schemeVersion !== "v2" || !state.dek) {
    throw new Error("Not a v2 session");
  }
  const dek = base64ToBytes(state.dek);
  const newSalt = generateSalt();
  const newKEK = await deriveKEK(newPassword, newSalt);
  const newWrapped = await wrapDEK(dek, newKEK);
  const { plaintext, wire } = await buildRecoveryPayloads(dek);

  const response = await fetch(`${API_BASE}/auth/change-password-v2`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${state.accessToken}`,
      "X-DEK": state.dek,
    },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
      new_password_salt: bytesToBase64(newSalt),
      new_wrapped_dek: bytesToBase64(newWrapped),
      new_recovery_codes: wire,
    }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Change password failed (${response.status})`);
  }

  return { recoveryCodes: plaintext };
}

/** Generate 8 new recovery codes (password unchanged). Returns plaintext. */
export async function rotateRecoveryCodesV2(): Promise<{
  recoveryCodes: string[];
}> {
  const state = getAuthState();
  if (!state) throw new Error("Not logged in");
  if (state.schemeVersion !== "v2" || !state.dek) {
    throw new Error("Not a v2 session");
  }
  const dek = base64ToBytes(state.dek);
  const { plaintext, wire } = await buildRecoveryPayloads(dek);

  const response = await fetch(`${API_BASE}/auth/rotate-recovery-codes`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${state.accessToken}`,
      "X-DEK": state.dek,
    },
    body: JSON.stringify({ new_recovery_codes: wire }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Rotate codes failed (${response.status})`);
  }

  return { recoveryCodes: plaintext };
}

/** Begin recovery: user enters email, server sends a 6-digit reset code. */
export async function recoverStart(email: string): Promise<void> {
  const response = await fetch(`${API_BASE}/auth/recover/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed (${response.status})`);
  }
}

/** Verify recovery: given (email, reset_token, recovery_code), unwrap DEK
 *  locally so the caller can commit a new password next. */
export async function recoverVerify(
  email: string,
  resetToken: string,
  recoveryCode: string
): Promise<{ dek: Uint8Array }> {
  const response = await fetch(`${API_BASE}/auth/recover/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      reset_token: resetToken,
      recovery_code: recoveryCode,
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Verify failed (${response.status})`);
  }
  const body = (await response.json()) as RecoverVerifyResponse;
  const salt = base64ToBytes(body.recovery_salt);
  const kek = await deriveKEK(normalizeRecoveryCode(recoveryCode), salt);
  const dek = await unwrapDEK(base64ToBytes(body.wrapped_dek), kek);
  return { dek };
}

/** Commit the recovery: new password + 8 fresh recovery codes, atomic. */
export async function recoverCommit(
  email: string,
  resetToken: string,
  newPassword: string,
  dek: Uint8Array
): Promise<{ recoveryCodes: string[] }> {
  const newSalt = generateSalt();
  const newKEK = await deriveKEK(newPassword, newSalt);
  const newWrapped = await wrapDEK(dek, newKEK);
  const { plaintext, wire } = await buildRecoveryPayloads(dek);

  const response = await fetch(`${API_BASE}/auth/recover/commit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      reset_token: resetToken,
      new_password: newPassword,
      new_password_salt: bytesToBase64(newSalt),
      new_wrapped_dek: bytesToBase64(newWrapped),
      new_recovery_codes: wire,
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Commit failed (${response.status})`);
  }
  return { recoveryCodes: plaintext };
}

/** Opt into admin-debug by wrapping the DEK under every current admin
 *  pubkey returned on the last login/refresh. */
export async function adminDebugEnable(): Promise<void> {
  const state = getAuthState();
  if (!state) throw new Error("Not logged in");
  if (state.schemeVersion !== "v2" || !state.dek) {
    throw new Error("Not a v2 session");
  }
  if (!state.adminPubkeys.length) {
    throw new Error("No admin keys configured");
  }
  const dek = base64ToBytes(state.dek);
  const wraps: Array<{ fingerprint: string; wrapped_dek: string }> = [];
  for (const key of state.adminPubkeys) {
    const wrapped = await wrapDEKForAdminPubkey(dek, key.pem);
    wraps.push({
      fingerprint: key.fingerprint,
      wrapped_dek: bytesToBase64(wrapped),
    });
  }

  const response = await fetch(`${API_BASE}/auth/admin-debug/enable`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${state.accessToken}`,
      "X-DEK": state.dek,
    },
    body: JSON.stringify({ admin_wrapped_deks: wraps }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Enable admin debug failed (${response.status})`);
  }
  setAuthState({ ...state, adminDebugEnabled: true });
}

export async function adminDebugDisable(): Promise<void> {
  const state = getAuthState();
  if (!state) throw new Error("Not logged in");
  const response = await fetch(`${API_BASE}/auth/admin-debug/disable`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${state.accessToken}`,
      ...(state.dek ? { "X-DEK": state.dek } : {}),
    },
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Disable admin debug failed (${response.status})`);
  }
  setAuthState({ ...state, adminDebugEnabled: false });
}

export async function adminDebugRewrap(): Promise<void> {
  const state = getAuthState();
  if (!state) throw new Error("Not logged in");
  if (state.schemeVersion !== "v2" || !state.dek) {
    throw new Error("Not a v2 session");
  }
  if (!state.adminPubkeys.length) {
    throw new Error("No admin keys configured");
  }
  const dek = base64ToBytes(state.dek);
  const wraps: Array<{ fingerprint: string; wrapped_dek: string }> = [];
  for (const key of state.adminPubkeys) {
    const wrapped = await wrapDEKForAdminPubkey(dek, key.pem);
    wraps.push({
      fingerprint: key.fingerprint,
      wrapped_dek: bytesToBase64(wrapped),
    });
  }
  const response = await fetch(`${API_BASE}/auth/admin-debug/rewrap`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${state.accessToken}`,
      "X-DEK": state.dek,
    },
    body: JSON.stringify({ admin_wrapped_deks: wraps }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Rewrap admin debug failed (${response.status})`);
  }
}

// Re-export so callers don't have to import from crypto_v2 directly.
export type { RecoveryPayloadWire };
